import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import {
  CompanyFinancialSummary,
  getCompanyFinancialSummary,
} from "src/apis/empresa";
import PageContainer from "src/pages/components/PageContainer";
import SearchAndResetButtons from "src/pages/components/SearchAndResetButtons";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import StartAndEndDateFilters from "src/pages/components/StartAndEndDateFilters";
import { getUserCompanyId, isUserCompany, isUserAdministrator } from "src/utils/auth";
import { formatCurrency } from "src/utils/currency";
import { formatDateToBR, getFirstDayOfMonth, getLastDayOfMonth } from "src/utils/date";

const FinancialSummaryPage: FC = () => {
  const [summary, setSummary] = useState<CompanyFinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [toastOpen, setToastOpen] = useState(false);

  const [startDate, setStartDate] = useState<string>(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState<string>(getLastDayOfMonth());

  const companyId = getUserCompanyId();
  const isCompanyUser = isUserCompany() && !isUserAdministrator();

  const fetchSummary = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    setError("");
    try {
      const data = await getCompanyFinancialSummary(companyId, {
        data_inicio: startDate,
        data_fim: endDate,
      });
      setSummary(data);
    } catch (err: any) {
      setError(err?.message || "Não foi possível carregar o resumo financeiro.");
    } finally {
      setIsLoading(false);
    }
  }, [companyId, startDate, endDate]);

  useEffect(() => {
    fetchSummary();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageContainer
      title="Resumo financeiro"
      error={error}
      guard={() => isCompanyUser}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          mb: 2,
        }}
      >
        <StartAndEndDateFilters
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <SearchAndResetButtons
          isLoading={isLoading}
          search={() => fetchSummary()}
          resetState={() => {
            setStartDate(getFirstDayOfMonth());
            setEndDate(getLastDayOfMonth());
          }}
        />
      </Box>

      {isLoading && <SkeletonLoad variant="table" rows={1} columns={7} />}

      {!isLoading && !summary && !error && (
        <Alert severity="info">Nenhum dado financeiro encontrado para o período.</Alert>
      )}

      {!isLoading && summary && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Período:{" "}
            <strong>
              {formatDateToBR(summary.periodo?.data_inicio)} –{" "}
              {formatDateToBR(summary.periodo?.data_fim)}
            </strong>
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Empresa</strong></TableCell>
                  <TableCell align="center"><strong>Sessões pendentes</strong></TableCell>
                  <TableCell align="right"><strong>Valor por sessão</strong></TableCell>
                  <TableCell align="right"><strong>Valor pendente</strong></TableCell>
                  <TableCell align="right"><strong>Valor consolidado</strong></TableCell>
                  <TableCell align="center"><strong>Mensalidade quitada</strong></TableCell>
                  <TableCell align="center"><strong>Ação</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{summary.empresa ?? "—"}</TableCell>
                  <TableCell align="center">{summary.sessoes_pendentes ?? 0}</TableCell>
                  <TableCell align="right">{formatCurrency(summary.valor_sessao)}</TableCell>
                  <TableCell align="right">{formatCurrency(summary.valor_mensal_pendente)}</TableCell>
                  <TableCell align="right">{formatCurrency(summary.valor_mensal_consolidado)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={summary.mensalidade_quitada ? "Quitada" : "Pendente"}
                      color={summary.mensalidade_quitada ? "success" : "warning"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      disabled={summary.mensalidade_quitada}
                      onClick={() => setToastOpen(true)}
                    >
                      Pagar
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setToastOpen(false)}>
          Funcionalidade de pagamento em breve.
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default FinancialSummaryPage;
